import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminService } from '../../services/adminService';
import { FiTrendingUp, FiDollarSign, FiUsers, FiActivity } from 'react-icons/fi';

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await adminService.getAnalytics();
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      // Fallback to empty data
      setAnalyticsData({
        revenue: { monthly: 0, annual: 0, byMonth: [] },
        users: { total: 0, active: 0, inactive: 0, growth: [] },
        packages: { trial: 0, base: 0, basicPlus: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Analytics & Reports">
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Analytics & Reports">
      <div className="space-y-6">
        {/* Revenue & User Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Analytics */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Analytics</h3>
              <FiDollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <span className="text-gray-700 font-medium">Monthly Revenue</span>
                <span className="text-2xl font-bold text-green-600">
                  BHD {analyticsData.revenue.monthly.toFixed(3)}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <span className="text-gray-700 font-medium">Annual Revenue (Projected)</span>
                <span className="text-xl font-semibold text-gray-900">
                  BHD {analyticsData.revenue.annual.toFixed(3)}
                </span>
              </div>
            </div>
          </div>

          {/* User Analytics */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">User Analytics</h3>
              <FiUsers className="w-6 h-6 text-blue-600" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <span className="text-gray-700 font-medium">Total Merchants</span>
                <span className="text-2xl font-bold text-blue-600">{analyticsData.users.total}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Active</span>
                <span className="text-lg font-semibold text-green-600">{analyticsData.users.active}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Inactive</span>
                <span className="text-lg font-semibold text-gray-600">{analyticsData.users.inactive}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Package Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Package Distribution</h3>
            <FiActivity className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Trial Users</div>
              <div className="text-3xl font-bold text-blue-600">{analyticsData.packages.trial}</div>
              <div className="text-xs text-gray-500 mt-1">Free 30-day trial</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Base Package</div>
              <div className="text-3xl font-bold text-green-600">{analyticsData.packages.base}</div>
              <div className="text-xs text-gray-500 mt-1">BHD 5.000/month</div>
            </div>
            <div className="p-4 bg-indigo-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Pro Package</div>
              <div className="text-3xl font-bold text-indigo-600">{analyticsData.packages.basicPlus}</div>
              <div className="text-xs text-gray-500 mt-1">BHD 10.000/month</div>
            </div>
          </div>
        </div>

        {/* Revenue by Month Chart */}
        {analyticsData.revenue.byMonth && analyticsData.revenue.byMonth.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Trend (Last 6 Months)</h3>
              <FiTrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="space-y-3">
              {analyticsData.revenue.byMonth.map((item, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-24 text-sm font-medium text-gray-600">{item.month}</div>
                  <div className="flex-1">
                    <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${Math.min((item.revenue / 1000) * 100, 100)}%` }}
                      >
                        <span className="text-xs text-white font-medium">
                          {item.revenue > 0 ? `BHD ${item.revenue.toFixed(3)}` : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="w-32 text-right">
                    <div className="text-xs text-gray-500">
                      Base: {item.baseCount} | Pro: {item.basicPlusCount}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Growth Chart */}
        {analyticsData.users.growth && analyticsData.users.growth.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">User Growth (Last 6 Months)</h3>
              <FiUsers className="w-6 h-6 text-blue-600" />
            </div>
            <div className="space-y-3">
              {analyticsData.users.growth.map((item, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-24 text-sm font-medium text-gray-600">{item.month}</div>
                  <div className="flex-1">
                    <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${Math.min((item.newMerchants / 50) * 100, 100)}%` }}
                      >
                        <span className="text-xs text-white font-medium">
                          {item.newMerchants > 0 ? `+${item.newMerchants}` : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="w-32 text-right text-sm font-semibold text-gray-900">
                    {item.newMerchants} new merchants
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AnalyticsPage;
